import { has, orderBy } from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import { Component, Fragment } from 'react';
import { OverlayTrigger, Row } from 'react-bootstrap';
import { RuntimeContext } from '../../../../../common/RuntimeContext';
import Image from '../../../../shared/components/Image';
import VerbButton, { VERB_BUTTON_TYPE } from '../../../../shared/components/VerbButton';
import SearchFormV2 from '../../../../shared/containers/forms/SearchFormV2';
import copyIdToClipboard from '../../../../shared/utils/AdminUtils';
import ManageTable from '../../../components/ManageTable';
import ArchivePopover from './ArchivePopover';

class CollectionTable extends Component {
    static propTypes = {
        isContentAdmin: PropTypes.bool.isRequired,
        isVerbAdmin: PropTypes.bool.isRequired,
        archiveCollection: PropTypes.func.isRequired,
        getCollectionStatus: PropTypes.func.isRequired,
        history: PropTypes.object.isRequired,
        viewableCollections: PropTypes.array,
        clearSearchedCollections: PropTypes.func.isRequired,
        getFilteredCollections: PropTypes.func.isRequired,
        copyCollection: PropTypes.func
    };

    static contextType = RuntimeContext;

    constructor(props) {
        super(props);
        this.state = {
            sortedCollections: orderBy(props.viewableCollections, ['name'], ['asc']),
            collectionsStatusById: {}
        };
    }

    componentDidUpdate(prevProps) {
        if (prevProps.viewableCollections !== this.props.viewableCollections) {
            this.setState({
                sortedCollections: orderBy(this.props.viewableCollections, ['name'], ['asc'])
            });
        }
    }

    _handleSearchResults = (hits) => {
        if (hits && hits.length) {
            const formattedHits = hits.reduce((acc, hit) => {
                acc._id.push(hit._id);
                return acc;
            }, {_id: []});
            this.props.getFilteredCollections({...formattedHits, pathsContext: this.context.pathsWeb}).then((newCollections) => {
                if (has(hits, '_id')) {
                    this.setState({
                        noResults: hits._id.length === 0
                    });
                } else {
                    this.setState({
                        noResults: true
                    });
                }
            });
        } else {
            // When search is cleared (empty results), reset to show all collections
            this.props.clearSearchedCollections();
            this.setState({
                noResults: false
            });
        }
    };

    // TODO: rewrite to follow new manage table paradigm (see LearningAssetTableV2.
    _makeCollectionColumnProperties = () => {
        const { collectionsStatusById } = this.state;
        const { getCollectionStatus, isContentAdmin, isVerbAdmin, archiveCollection, copyCollection } = this.props;
        return [
            {
                columnName: 'Actions',
                columnClassName: 'buttonCell',
                columnRowClassName: 'buttonCell',
                columnRowRenderer: (collection) => {
                    const archiveButton = (
                        <OverlayTrigger
                            trigger={'click'}
                            placement={'bottom'}
                            rootClose
                            transition={false}
                            overlay={
                                <ArchivePopover
                                    collection={collection}
                                    collectionsStatusById={collectionsStatusById}
                                    archiveCollection={archiveCollection}
                                />
                            }
                        >
                            <VerbButton
                                className={'archive-button'}
                                buttonType={VERB_BUTTON_TYPE.SECONDARY_DETAILS}
                                fontAwesomeIcon={'fa-archive'}
                                onClick={() =>
                                    getCollectionStatus({collectionId: collection._id, pathsContext: this.context.pathsWeb}).then((collectionStatus) => {
                                        this.setState({
                                            collectionsStatusById: {
                                                ...this.state.collectionsStatusById,
                                                [collection._id]: collectionStatus
                                            }
                                        });
                                    })
                                }
                            />
                        </OverlayTrigger>
                    );
                    return (
                        <Fragment>
                            <VerbButton
                                buttonType={VERB_BUTTON_TYPE.SECONDARY_DETAILS}
                                fontAwesomeIcon={'fa-pencil'}
                                onClick={() => this.props.history.push(`/editcollection/${collection._id}`)}
                            />
                            <VerbButton
                                buttonType={VERB_BUTTON_TYPE.SECONDARY_DETAILS}
                                fontAwesomeIcon={'fa-eye'}
                                onClick={() =>
                                    window.open(`${this.context.pathsWeb.clientUrl}/collection/${collection._id}`)
                                }
                            />
                            <VerbButton
                                buttonType={VERB_BUTTON_TYPE.SECONDARY_DETAILS}
                                fontAwesomeIcon={'fa-clone'}
                                onClick={() => copyCollection(collection)}
                            />
                            {(isContentAdmin || isVerbAdmin) && archiveButton}
                        </Fragment>
                    );
                }
            },
            {
                columnName: 'COPY ID',
                columnPropertyName: '_id',
                columnClassName: 'copyIdButtonCell',
                columnRowClassName: 'copyIdButtonCell',
                columnRowRenderer: (collection) => (
                    <VerbButton
                        buttonType={VERB_BUTTON_TYPE.SECONDARY_DETAILS}
                        className={'key-button copy-id-button'}
                        fontAwesomeIcon={'fa-clipboard'}
                        onClick={() => {
                            copyIdToClipboard(collection._id);
                        }}
                    />
                )
            },
            {
                columnName: 'Name',
                columnClassName: 'collection-title',
                columnRowClassName: 'collection-title',
                columnRowRenderer: (collection) => {
                    const imageUrl = collection.thumbnailImageUrl || '/static/images/placeholderSnippetOld.jpg';
                    return (
                        <Fragment>
                            <Image
                                src={imageUrl}
                                className={'trackCoverImage'}
                                dimension={{ h: 60, w: 60 }}
                                alt={'trackCoverImage'}
                            />
                            {collection.name}
                        </Fragment>
                    );
                }
            },
            {
                columnName: 'Updated On',
                columnRowRenderer: (collection) => moment(collection.updatedOn).format('MMM Do YYYY')
            },
            {
                columnName: 'Is Active?',
                columnRowClassName: 'text-center',
                columnRowRenderer: (collection) => (collection.isActive ? <i className={'fal fa-check-circle'} /> : '')
            }
        ];
    };

    _handleSearchSubmit = () => {
        this.props.clearSearchedCollections();
    };

    render() {
        const { sortedCollections } = this.state;

        return (
            <div className={'collection-table'}>
                <Row>
                    <div className={'col-4 col-sm-4 col-md-4 trackListItem'}>
                        <VerbButton
                            buttonType={VERB_BUTTON_TYPE.PRIMARY_DEFAULT}
                            onClick={() => this.props.history.push('/newcollection')}
                            fontAwesomeIcon={'far fa-plus'}
                        >
                            NEW PATH
                        </VerbButton>
                    </div>
                    <div className={'col-4 ms-auto'}>
                        <SearchFormV2
                            indexName={'Collections'}
                            keyId={'wh7rFih2MkcbNrTPkvre'}
                            handleSearchResults={this._handleSearchResults}
                            onClearSearch={this._handleSearchSubmit}
                            placeholderTextId={'MANAGE_SEARCH'}
                            placeholderDefault={'Search'}
                            pathsContext={this.context.pathsWeb}
                            autoUpdateField={false}
                        />
                    </div>
                </Row>
                <div className={'trackListing col-12'}>
                    <ManageTable
                        isStriped={false}
                        isBordered={false}
                        isHover={true}
                        getRowKey={(collection) => collection._id}
                        columnProperties={this._makeCollectionColumnProperties()}
                        objectArray={sortedCollections}
                    />
                    {!this.state.sortedCollections.length && <div> No results found </div>}
                </div>
            </div>
        );
    }
}

export default CollectionTable;
